
import { motion, useTransform, type MotionValue } from "framer-motion";
import {
  EDGE,
  FINDING_STROKE,
  MOBILE,
  NODE_CARD,
  ZONE,
} from "./graph-layout";
import {
  KEYFRAME_BANDS,
} from "./keyframe-data";
import { useBeatIndex } from "./beat-index";
import { ModalLayer } from "./elements/modal-layer";
import { NodeCard, type ServiceIconId } from "./elements/node-card";
import { PeripheralCard } from "./elements/peripheral-card";
import type { KeyframeBand } from "./types";
import { graphSceneForBeat } from "./graph-scene";

interface Props {
  progress: MotionValue<number>;
  bands?: readonly KeyframeBand[];
}

const X = {
  center: 105,
  left: 24,
  right: 186,
} as const;

const Y = {
  api: 42,
  lambda: 132,
  ai: 232,
  data: 340,
  snapshot: 456,
} as const;

const MOBILE_PERIPHERALS = [
  { id: "rds", label: "RDS", x: 24, y: 50, opacity: 0.9 },
  { id: "ecs", label: "ECS", x: 208, y: 84, opacity: 0.72 },
  { id: "sqs", label: "SQS", x: 58, y: 166, opacity: 0.78 },
  { id: "cognito", label: "Cognito", x: 222, y: 218, opacity: 0.7 },
  { id: "cw-logs", label: "CloudWatch", x: 16, y: 286, opacity: 0.64 },
  { id: "step-fns", label: "Step Fns", x: 190, y: 320, opacity: 0.68 },
  { id: "kinesis", label: "Kinesis", x: 42, y: 422, opacity: 0.7 },
  { id: "secrets", label: "Secrets Mgr", x: 210, y: 470, opacity: 0.62 },
  { id: "sns", label: "SNS", x: 18, y: 558, opacity: 0.68 },
  { id: "ses", label: "SES", x: 206, y: 594, opacity: 0.6 },
] as const;

const MOBILE_FOCUSED_NODES = [
  { id: "api-gw", x: 105, y: 42, settledX: X.center, settledY: Y.api },
  { id: "lambda", x: 32, y: 178, settledX: X.center, settledY: Y.lambda },
  { id: "s3", x: 186, y: 272, settledX: X.left, settledY: Y.data },
  { id: "dynamo", x: 42, y: 398, settledX: X.right, settledY: Y.data },
  { id: "ddb-snap", x: 186, y: 520, settledX: X.right, settledY: Y.snapshot },
] satisfies readonly {
  id: ServiceIconId;
  x: number;
  y: number;
  settledX: number;
  settledY: number;
}[];

function edgePath(points: readonly [number, number][]): string {
  return points.map(([x, y], index) => `${index === 0 ? "M" : "L"}${x},${y}`).join(" ");
}

function FlowEdge({
  d,
  lit = false,
  drift = false,
  opacity = 1,
}: {
  d: string;
  lit?: boolean;
  drift?: boolean;
  opacity?: number;
}) {
  return (
    <path
      d={d}
      fill="none"
      stroke={drift ? "#C0392B" : lit ? FINDING_STROKE : EDGE.flow}
      strokeWidth={lit || drift ? EDGE.flowLitWidth : EDGE.flowWidth}
      strokeDasharray={EDGE.copyDash}
      strokeLinejoin="round"
      strokeLinecap="round"
      markerEnd={drift ? "url(#arrow-drift-mobile)" : lit ? "url(#arrow-flow-lit-mobile)" : "url(#arrow-flow-mobile)"}
      opacity={opacity}
    />
  );
}

function MotionNodeCard({
  id,
  x,
  y,
  settledX,
  settledY,
  settled,
  opacity,
  hideResourceId,
  state = "default",
}: {
  id: ServiceIconId;
  x: number;
  y: number;
  settledX: number;
  settledY: number;
  settled: boolean;
  opacity: number;
  hideResourceId: boolean;
  state?: Parameters<typeof NodeCard>[0]["state"];
}) {
  return (
    <motion.g
      initial={false}
      animate={{
        x: settled ? settledX : x,
        y: settled ? settledY : y,
        opacity,
      }}
      transition={{ duration: 0.28, ease: "easeOut" }}
    >
      <NodeCard id={id} x={0} y={0} state={state} hideResourceId={hideResourceId} />
    </motion.g>
  );
}

export function MobileVerticalGraph({ progress, bands = KEYFRAME_BANDS }: Props) {
  const beatIndex = useBeatIndex(progress, bands);
  const scene = graphSceneForBeat(beatIndex);
  // bands[1] is the scan band — always present in the 6-beat KEYFRAME_BANDS constant.
  const band1 = bands[1]!;
  const scanY = useTransform(progress, [band1.start, band1.end], [-180, 780]);
  const scanOpacity = useTransform(
    progress,
    [band1.start, band1.start + 0.02, band1.end - 0.02, band1.end],
    [0, 1, 1, 0],
  );
  const showInventory = scene.showInventory;
  const showGraph = scene.showGraph;
  const showWorkload = scene.showWorkload;
  const showDrift = scene.showDrift;
  const showFinding = scene.showFinding;
  const dimBase = scene.dimBase ? 0.72 : 1;

  return (
    <g aria-hidden="true">
      {!showGraph ? (
        <motion.g initial={false} animate={{ opacity: showInventory ? 0.55 : 0.35 }}>
          {MOBILE_PERIPHERALS.map((node) => (
            <PeripheralCard
              key={node.id}
              id={node.id}
              label={node.label}
              x={node.x}
              y={node.y}
              opacity={node.opacity}
            />
          ))}
        </motion.g>
      ) : null}

      {showInventory && !showGraph ? (
        <motion.g style={{ opacity: scanOpacity }} aria-hidden="true">
          <motion.rect
            style={{ y: scanY }}
            x={0}
            width={MOBILE.viewBox.w}
            height={160}
            fill="url(#mobile-scan-sweep)"
          />
        </motion.g>
      ) : null}

      {showWorkload ? (
        <motion.g initial={false} animate={{ opacity: 1 }}>
          <rect
            x={MOBILE.zone.x}
            y={MOBILE.zone.y}
            width={MOBILE.zone.w}
            height={MOBILE.zone.h}
            rx={ZONE.radius}
            fill={ZONE.fill}
            stroke={ZONE.stroke}
            strokeWidth={ZONE.strokeWidth}
            strokeDasharray={ZONE.dash}
          />
        </motion.g>
      ) : null}

      {showGraph ? (
        <motion.g initial={false} animate={{ opacity: dimBase }}>
          <FlowEdge
            d={edgePath([
              [180, Y.api + NODE_CARD.height],
              [180, Y.lambda],
            ])}
          />
          <FlowEdge
            d={edgePath([
              [180, Y.lambda + NODE_CARD.height],
              [180, 210],
              [70, 210],
              [70, 304],
              [99, Y.data],
            ])}
          />
          <FlowEdge
            d={edgePath([
              [180, Y.lambda + NODE_CARD.height],
              [180, 210],
              [290, 210],
              [290, 304],
              [261, Y.data],
            ])}
          />
          <FlowEdge
            d={edgePath([
              [261, Y.data + NODE_CARD.height],
              [261, Y.snapshot],
            ])}
            lit={showFinding}
          />
        </motion.g>
      ) : null}

      <motion.g initial={false} animate={{ opacity: dimBase }}>
        {MOBILE_FOCUSED_NODES.map((node) => {
          const state =
            node.id === "dynamo" && showFinding
              ? "focus"
              : node.id === "ddb-snap" && showFinding
                ? "affected"
                : (node.id === "s3" || node.id === "dynamo" || node.id === "ddb-snap") && showWorkload
                ? "workload"
                : "default";

          return (
            <MotionNodeCard
              key={node.id}
              id={node.id}
              x={node.x}
              y={node.y}
              settledX={node.settledX}
              settledY={node.settledY}
              settled={showGraph}
              opacity={showGraph ? 1 : showInventory ? 0.82 : 0.62}
              hideResourceId={!showInventory}
              state={state}
            />
          );
        })}
      </motion.g>

      {showDrift ? (
        <motion.g initial={false} animate={{ opacity: 1 }}>
          <FlowEdge
            d={edgePath([
              [180, Y.lambda + NODE_CARD.height],
              [180, Y.ai],
            ])}
            drift
          />
          <NodeCard id="bedrock" x={X.center} y={Y.ai} state="drift" />
        </motion.g>
      ) : null}

      <ModalLayer modal={scene.modal} x={MOBILE.modal.x} y={MOBILE.modal.y} w={MOBILE.modal.w} />
    </g>
  );
}
